/**
 * Savable sections — the seam between the workspace hook and sections that
 * hold unsaved edits (#340 item 2).
 *
 * Before generating a job summary the workspace must flush every section's
 * pending edits. Previously the hook reached into each section through a
 * differently-shaped imperative ref (CrewSaveHandle, JobDetailsSaveHandle),
 * coupling it to section internals; adding a savable section meant threading
 * a new ref end to end. Now sections self-register a save function and the
 * hook only knows `saveAll()` — new sections plug in without touching it.
 *
 * Contract:
 * - `register` returns an unregister cleanup (a section that unmounts simply
 *   stops being saved — the old `ref.current ?? Promise.resolve()` skip).
 * - `saveAll` runs EVERY registered save even when one fails (sections are
 *   independent; one failure shouldn't strand another's edits), then rejects
 *   with the first failure so the caller can abort what follows.
 * - The registry object is referentially stable for the hook's lifetime.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";

export type SaveSection = () => Promise<void>;

export interface SavableSections {
  /** Register a section's save; returns the unregister cleanup. */
  register: (key: string, save: SaveSection) => () => void;
  /** Save every registered section; rejects with the first failure. */
  saveAll: () => Promise<void>;
}

export function useSavableSections(): SavableSections {
  const sectionsRef = useRef(new Map<string, SaveSection>());

  const register = useCallback((key: string, save: SaveSection) => {
    sectionsRef.current.set(key, save);
    return () => {
      // Only delete our own registration — a re-register under the same key
      // (remount) must not be wiped by the stale cleanup that follows it.
      if (sectionsRef.current.get(key) === save) {
        sectionsRef.current.delete(key);
      }
    };
  }, []);

  const saveAll = useCallback(async () => {
    const saves = [...sectionsRef.current.values()];
    const results = await Promise.allSettled(saves.map((save) => save()));
    const rejected = results.find((r) => r.status === "rejected");
    if (rejected && rejected.status === "rejected") throw rejected.reason;
  }, []);

  return useMemo(() => ({ register, saveAll }), [register, saveAll]);
}

/**
 * Section-side adapter: keep this section registered while mounted. The
 * latest `save` is always the one invoked (tracked via ref), so sections can
 * pass a plain closure over their current state — no useCallback, no
 * state-mirroring refs.
 */
export function useRegisterSavable(
  sections: SavableSections | undefined,
  key: string,
  save: SaveSection,
): void {
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    if (!sections) return;
    return sections.register(key, () => saveRef.current());
  }, [sections, key]);
}
